import {
  OnDemandVolumeEstimate,
  StorageTime,
  SubmitOnDemandLeadError
} from 'src/types/graphql'
import { DEFAULT_PER_SQM_RATES, OnDemandCityConfig } from 'src/utils/onDemand'

/**
 * Pure helpers for the on-demand lead wizard. Kept dependency-free so the
 * test suite (`__tests__/wizardHelpers.test.ts`) can exercise them via the
 * Node test runner without a React/Apollo/jsdom harness.
 */

/** Strip every non-digit character from a phone string. */
export function stripPhone(raw: string): string {
  return (raw ?? '').replace(/\D/g, '')
}

/**
 * Normalize a phone string to an E.164-ish form before sending it to the
 * `submitOnDemandLead` mutation. The backend also normalizes — this is just
 * for nicer client-side UX (consistent payload + no surprise diffs).
 *
 * Mexico-first rules, mirrors `applyAutoPrefix` in `usePhoneInput.ts`:
 *  - already starts with "+" → keep "+digits"
 *  - 11 digits starting with 1 → US/Canada paste, prepend "+"
 *  - 10 digits → assume Mexico, prepend "+52"
 *  - already starts with "52" + more digits → prepend "+"
 *  - otherwise → prepend "+52"
 */
export function normalizePhoneE164(raw: string): string {
  if (!raw) return ''
  const trimmed = raw.trim()
  if (trimmed.startsWith('+')) {
    return '+' + stripPhone(trimmed)
  }
  const digits = stripPhone(trimmed)
  if (!digits) return ''
  if (digits.length === 11 && digits.startsWith('1')) return '+' + digits
  if (digits.startsWith('52')) return '+' + digits
  if (digits.length === 10) return '+52' + digits
  return '+52' + digits
}

/**
 * Map a `SubmitOnDemandLeadError` enum value to the locale key under
 * `FORM.ERROR.*`. Returns `null` for errors that surface as field-level
 * validation instead of a generic toast.
 */
export function mapErrorToLocaleKey(
  error: SubmitOnDemandLeadError | null | undefined
): 'GENERIC' | 'CITY_NOT_COVERED' | 'RATE_LIMITED' | null {
  switch (error) {
    case SubmitOnDemandLeadError.CityNotCovered:
      return 'CITY_NOT_COVERED'
    case SubmitOnDemandLeadError.RateLimited:
      return 'RATE_LIMITED'
    case SubmitOnDemandLeadError.InvalidInput:
    case SubmitOnDemandLeadError.InvalidPhone:
      return null
    case SubmitOnDemandLeadError.HubspotFailure:
    case SubmitOnDemandLeadError.Generic:
    default:
      return 'GENERIC'
  }
}

/**
 * Map a `OnDemandVolumeEstimate` to a m² floor-footprint estimate, used by
 * the live price reveal in step 2. Numbers are based on the partner's
 * conversion notes: a small bundle (~30 cajas) fits in ~2 m², a medium
 * bundle (~recámara amueblada) ~4 m², a large bundle (~departamento) ~7 m².
 * `Unsure` returns `null` so the price reveal renders a "we'll help you
 * estimate" hint instead of a number.
 */
export function volumeToEstimatedM2(
  volume: OnDemandVolumeEstimate | null | undefined
): number | null {
  switch (volume) {
    case OnDemandVolumeEstimate.Small:
      return 2
    case OnDemandVolumeEstimate.Medium:
      return 4
    case OnDemandVolumeEstimate.Large:
      return 7
    case OnDemandVolumeEstimate.Unsure:
    default:
      return null
  }
}

/**
 * Map a `StorageTime` to the partner's per-m² duration tier (MXN/m²/mes).
 * The rates themselves are PER MARKET and live in `onDemandCoverage`
 * (`perSqmRates`) — this only picks which tier applies. Storage times longer
 * than `FewMonthsTwelve` (`Year`, `FewYears`, `Indefinitely`) collapse to the
 * 12+ tier — they're all the same partner price floor.
 */
export type DurationTierKey = 'monthToMonth' | 'sixMonths' | 'twelveMonths'

export function storageTimeToTier(
  storageTime: StorageTime | null | undefined
): DurationTierKey | null {
  switch (storageTime) {
    case StorageTime.Month:
    case StorageTime.FewMonths:
      return 'monthToMonth'
    case StorageTime.FewMonthsSix:
      return 'sixMonths'
    case StorageTime.FewMonthsTwelve:
    case StorageTime.Year:
    case StorageTime.FewYears:
    case StorageTime.Indefinitely:
      return 'twelveMonths'
    default:
      return null
  }
}

/**
 * Discount of `tier` vs month-to-month for one market's rates, as a rounded
 * whole percentage. Every "−N%" on the on-demand surfaces (duration table,
 * wizard duration options, price-reveal nudge) comes from here — never from
 * copy — because partners discount differently: Monterrey's gives −20% at 6
 * months and −30% at 12, CDMX's one "precio especial" from 3 months, so its
 * 6- and 12-month tiers are both −25%. A hardcoded "−30%" overstated CDMX.
 */
export function getTierDiscountPct(
  rates: OnDemandCityConfig['perSqmRates'],
  tier: DurationTierKey
): number {
  if (rates.monthToMonth <= 0) return 0
  return Math.max(0, Math.round((1 - rates[tier] / rates.monthToMonth) * 100))
}

/**
 * Resolve the per-m² rate (MXN/m²/mes) for the given storage-time enum,
 * pulled from the city's `perSqmRates` if available, falling back to the
 * partner's published rates so the wizard still shows a number even when
 * the city config hasn't loaded yet (e.g. SSR hydration window).
 */
export function getRateForDuration(
  storageTime: StorageTime | null | undefined,
  perSqmRates: OnDemandCityConfig['perSqmRates'] | undefined
): number | null {
  const tier = storageTimeToTier(storageTime)
  if (!tier) return null
  if (perSqmRates) return perSqmRates[tier]
  // Fallback to the shared default card — never a second copy of the numbers,
  // which would silently drift from whichever market re-bases first.
  return DEFAULT_PER_SQM_RATES[tier]
}
